import { OneTimeSecretDisplay } from '@/components/common/OneTimeSecretDisplay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import type { Option } from '@/components/ui/multiple-selector'
import MultipleSelector from '@/components/ui/multiple-selector'
import { useWebhookActions } from '@/features/webhooks/hooks/useWebhookActions'
import { useRequiredOrgId } from '@/hooks/useRequiredOrgId'
import { zodResolver } from '@hookform/resolvers/zod'
import { webhookEventTypeSchema } from '@server/types/webhookEventTypes.ts'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

const OPTIONS: Option[] = webhookEventTypeSchema.options.map((event) => ({
  value: event,
  label: event,
}))

const optionSchema = z.object({
  value: z.enum(webhookEventTypeSchema.options),
  label: z.string(),
})

const formSchema = z.object({
  // NOTE: Calling this `name` will break the form.
  webhook_name: z
    .string()
    .min(3, 'Webhook name must be at least 3 characters long')
    .max(255, 'Webhook name must be less than 255 characters'),
  url: z.string().url('Invalid URL'),
  event_types: z.array(optionSchema).min(1, 'At least one event is required'),
})

export const CreateWebhookButton = () => {
  const organizationId = useRequiredOrgId()
  const [open, setOpen] = useState(false)
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null)
  const { createWebhook } = useWebhookActions()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      webhook_name: '',
      url: '',
      event_types: [],
    },
  })

  useEffect(() => {
    if (!open) {
      form.reset()
      setWebhookSecret(null)
    }
  }, [open, form])

  function onSubmit(values: z.infer<typeof formSchema>) {
    createWebhook.mutate(
      {
        organizationId,
        name: values.webhook_name,
        url: values.url,
        event_types: values.event_types.map((event) => event.value),
      },
      {
        onSuccess: (data) => {
          if (!data) {
            throw new Error('Failed to create webhook')
          }

          setWebhookSecret(data.secret)
          toast.success('Webhook created', {
            description: 'Webhook created successfully',
          })
        },
        onError: (error) => {
          toast.error('Error creating webhook', {
            description: error.message,
          })
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4" />
          Add Webhook
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Webhook</DialogTitle>
          <DialogDescription>
            Configure your webhook endpoint to receive notifications
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          {webhookSecret && (
            <OneTimeSecretDisplay
              secret={webhookSecret}
              label="Webhook Secret"
              helpText="Use this secret to verify webhook signatures in your application."
              onDone={() => setOpen(false)}
            />
          )}

          {!webhookSecret && (
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="pt-2 space-y-4"
            >
              <FormField
                control={form.control}
                name="webhook_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="My webhook" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://your-domain.com/webhook"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="event_types"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Events</FormLabel>
                    <FormControl>
                      <MultipleSelector
                        {...field}
                        defaultOptions={OPTIONS}
                        placeholder="Select events"
                        emptyIndicator={
                          <p className="text-lg leading-10 text-center text-gray-600 dark:text-gray-400">
                            no results found.
                          </p>
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={createWebhook.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
